import type { BreadcrumbList, WithContext } from 'schema-dts';
import type { BreadCrumb } from '~/components/common/types';

export function useGoogleBreadcrumb(
  websiteUrl: string,
  locale: string,
  homeTitle: string,
  crumbs: Array<BreadCrumb>,
  currentPath: string,
): WithContext<BreadcrumbList> | undefined {
  if (crumbs == null || crumbs.length === 0) {
    return undefined;
  }

  // Mirror the visible Breadcrumb component 1:1: a Home item followed by every
  // crumb, using locale-prefixed absolute URLs.
  const items: Array<BreadCrumb> = [{ title: homeTitle, link: '' }, ...crumbs];
  const lastIndex = items.length - 1;

  const itemUrl = (crumb: BreadCrumb, index: number): string => {
    if (index === 0) {
      return `${websiteUrl}/${locale}`;
    }

    // The last crumb is the current page. Use its real URL instead of
    // reconstructing it from a link that may be empty or already include a
    // leading slash or locale segment.
    if (index === lastIndex) {
      return `${websiteUrl}${currentPath}`;
    }

    // Treat the link as a relative path so a leading slash can't produce a
    // double slash after the locale segment.
    return `${websiteUrl}/${locale}/${crumb.link.replace(/^\/+/, '')}`;
  };

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.title,
      item: itemUrl(crumb, index),
    })),
  } as WithContext<BreadcrumbList>;
}
